/**
 * Panel szczegółów zadania KPI — odpowiednik SubTaskDetailPanel z Friday: ten sam układ
 * (pełnoekranowa nakładka, edytowalny tytuł w nagłówku, lewa kolumna z polami, prawa z
 * rozmową), żeby klik w wiersz robił to samo, czego ludzie nauczyli się na tablicy.
 */
import { useEffect, useMemo, useState } from "react";
import { Bell, ChevronDown, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { WeekSelect } from "@/components/ui/WeekSelect";
import { UserAvatar } from "@/components/ui/UserAvatar";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import { PRIORITY_COLORS, PRIORITY_LABELS } from "@/features/tasks/types";
import { useAuthStore } from "@/store/auth";
import { useInternalUsers } from "@/features/tasks/hooks/useInternalUsers";
import {
  useKpiActionItemMutations,
  useKpiItemCommentMutations,
  useKpiItemComments,
  useKpiItemPings,
} from "../hooks";
import type { KpiActionItem, KpiActionPriority } from "../types";
import { canPingKpi, fmtDate, fmtDateTime, isDirectorateKpiItem, kpiOriginHint } from "../utils";
import { CommentBody } from "./CommentBody";
import { KPI_ACTION_LABELS, KPI_ACTION_ORDER, STATUS_CELL, isKpiUnfinished } from "./ui";

function Label({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
      {children}
    </span>
  );
}

/**
 * Historia ponagleń pod zadaniem — kto, kiedy i do kogo (user, 12.09.2026: „pod taskiem KPI
 * niech widać historię pingów"). Widzi ją KAŻDY, kto widzi zadanie, także pingowany:
 * ponaglenie nie jest anonimowe. Przycisk „Ponagl" pokazujemy tylko dyrekcji technicznej
 * i zarządowi — bramkę i tak trzyma backend.
 */
function PingSection({ item, connectionId }: { item: KpiActionItem; connectionId: string }) {
  const { data: pings = [], isLoading } = useKpiItemPings(item.id);
  const m = useKpiActionItemMutations(connectionId);
  const canPing = canPingKpi(useAuthStore((s) => s.user));
  const pending = m.ping.isPending;

  if (!canPing && pings.length === 0) return null;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-3">
        <Label>Ponaglenia {pings.length > 0 ? `(${pings.length})` : ""}</Label>
        {canPing && (
          <Button
            size="sm"
            variant="secondary"
            disabled={pending}
            onClick={() =>
              m.ping.mutate(item.id, {
                onError: () => alert("Nie udało się wysłać ponaglenia. Spróbuj ponownie."),
              })
            }
          >
            <Bell size={13} className="mr-1 inline" />
            {pending ? "Wysyłam…" : "Ponagl"}
          </Button>
        )}
      </div>
      {isLoading ? (
        <p className="font-mono text-[11px] text-gray-400">Wczytywanie…</p>
      ) : pings.length === 0 ? (
        <p className="font-mono text-[11px] text-gray-400 dark:text-gray-500">
          Nikt jeszcze nie ponaglał tego zadania.
        </p>
      ) : (
        <ul className="flex flex-col gap-1">
          {pings.map((p) => (
            <li
              key={p.id}
              className="border-l-2 border-[var(--color-primary)] pl-2 font-mono text-[11px] text-gray-500 dark:text-gray-400"
            >
              <span className="font-semibold text-[var(--color-primary)]">{p.by_name}</span>
              {" · "}
              {fmtDateTime(p.at)}
              {p.recipients.length > 0 && <> · do: {p.recipients.join(", ")}</>}
              {p.mail_status !== "sent" && (
                <> · mail: {p.mail_status === "queued" ? "w kolejce" : "nie wyszedł"}</>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function KpiItemDetailPanel({
  item,
  items,
  connectionId,
  onClose,
  onOpenOther,
}: {
  item: KpiActionItem;
  items: KpiActionItem[];
  connectionId: string;
  onClose: () => void;
  onOpenOther: (id: string) => void;
}) {
  const m = useKpiActionItemMutations(connectionId);
  const { data: users = [] } = useInternalUsers();
  const me = useAuthStore((s) => s.user);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(item.description);

  const { data: comments = [] } = useKpiItemComments(item.id);
  const cm = useKpiItemCommentMutations(item.id, connectionId);
  const [draft, setDraft] = useState("");
  // @oznaczenia — oznaczony dostaje powiadomienie z linkiem wprost do tego zadania.
  const [draftMentions, setDraftMentions] = useState<string[]>([]);

  // Esc zamyka — jak każdy panel w systemie.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    setTitleDraft(item.description);
    setEditingTitle(false);
  }, [item.id, item.description]);

  const userOptions = useMemo(
    () => [...users].sort((a, b) => a.full_name.localeCompare(b.full_name, "pl"))
      .map((u) => ({ value: u.id, label: u.full_name })),
    [users],
  );
  const mentionSuggestions = useMemo(
    () => users.map((u) => ({ id: u.id, name: u.full_name })),
    [users],
  );
  const byId = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  const children = useMemo(() => items.filter((i) => i.parent === item.id), [items, item.id]);
  // Ścieżka kubełków nad zadaniem — bez niej „Korekta PB" nic nie znaczy.
  const path = useMemo(() => {
    const out: KpiActionItem[] = [];
    let node = item.parent ? byId.get(item.parent) : undefined;
    let guard = 0;
    while (node && guard++ < 8) {
      out.unshift(node);
      node = node.parent ? byId.get(node.parent) : undefined;
    }
    return out;
  }, [item, byId]);

  const canEdit =
    !!me &&
    (me.is_ceo || (me.role === "director" && !!me.is_technical_director) || item.assignee === me.id);
  // Ta sama reguła co w drzewie i na liście globalnej — karta nie może mówić czegoś innego
  // niż wiersz, z którego się ją otworzyło.
  const directive = isDirectorateKpiItem(item);

  const patch = (payload: Partial<KpiActionItem>) =>
    m.update.mutate({ id: item.id, payload });

  const saveTitle = () => {
    const text = titleDraft.trim();
    setEditingTitle(false);
    if (text && text !== item.description) patch({ description: text });
  };

  const sendComment = () => {
    if (!draft.trim()) return;
    cm.create.mutate(
      { content: draft.trim(), mentionIds: draftMentions },
      { onSuccess: () => { setDraft(""); setDraftMentions([]); } },
    );
  };

  return (
    <>
      <div className="fixed inset-0 z-[75] bg-black/30" onClick={onClose} />
      <div className="fixed inset-0 z-[80] flex items-end justify-center p-0 sm:items-center sm:p-4">
        <div className="flex h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl sm:h-[88vh] sm:rounded-2xl dark:bg-gray-800">

          {/* Nagłówek */}
          <div className="flex flex-shrink-0 items-start gap-4 border-b border-gray-100 px-6 py-4 dark:border-gray-700">
            <div className="min-w-0 flex-1">
              <p className="mb-1 truncate text-xs text-gray-400 dark:text-gray-500" title={kpiOriginHint(item)}>
                <span className={`font-medium ${directive ? "text-[var(--color-primary)]" : "text-gray-400 dark:text-gray-500"}`}>
                  {item.origin === "kpi"
                    ? `KPI #${item.review_sequence_no}`
                    : directive
                      ? "Ustalenie dyrekcji"
                      : "Zapisek własny"}
                </span>
                {path.map((p) => (
                  <span key={p.id}>
                    {" · "}
                    <button
                      type="button"
                      className="hover:text-[var(--color-primary)] hover:underline"
                      onClick={() => onOpenOther(p.id)}
                    >
                      {p.description}
                    </button>
                  </span>
                ))}
              </p>
              {editingTitle && canEdit ? (
                <input
                  autoFocus
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  onBlur={saveTitle}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") saveTitle();
                    if (e.key === "Escape") { setTitleDraft(item.description); setEditingTitle(false); }
                  }}
                  className="w-full border-b-2 border-[var(--color-primary)] bg-transparent text-xl font-bold text-gray-900 outline-none dark:text-gray-100"
                />
              ) : (
                <h2
                  className={`text-xl font-bold leading-snug ${directive ? "text-[var(--color-primary)]" : "text-gray-900 dark:text-gray-100"} ${canEdit ? "cursor-pointer hover:text-[var(--color-primary)]" : ""}`}
                  onClick={() => canEdit && setEditingTitle(true)}
                  title={canEdit ? "Kliknij aby edytować" : "Edytować może autor, odpowiedzialny lub dyrektor techniczny"}
                >
                  {item.description}
                </h2>
              )}
            </div>
            <button onClick={onClose} className="mt-1 flex-shrink-0 p-1.5 text-gray-400 hover:text-gray-600 dark:text-gray-500">
              <X size={20} />
            </button>
          </div>

          {/* Ciało: lewa kolumna pola, prawa rozmowa */}
          <div className="flex flex-1 flex-col overflow-hidden md:flex-row">
            <div className="flex min-w-0 flex-1 flex-col gap-6 overflow-y-auto border-r border-gray-100 px-6 py-5 dark:border-gray-700">

              <div className="flex flex-wrap gap-4">
                <div className="flex flex-col gap-1.5">
                  <Label>Status</Label>
                  <div className="relative">
                    <select
                      value={item.status}
                      onChange={(e) => m.setStatus.mutate({ id: item.id, status: e.target.value })}
                      className={`cursor-pointer appearance-none rounded-lg border-0 px-4 py-2 pr-8 text-sm font-semibold shadow-sm ${STATUS_CELL[item.status]}`}
                    >
                      {KPI_ACTION_ORDER.map((s) => (
                        <option key={s} value={s}>{KPI_ACTION_LABELS[s]}</option>
                      ))}
                    </select>
                    <ChevronDown size={13} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2" />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label>Priorytet</Label>
                  <div className="relative">
                    <select
                      value={item.priority}
                      disabled={!canEdit}
                      onChange={(e) => patch({ priority: e.target.value as KpiActionPriority })}
                      className={`appearance-none rounded-lg border-0 px-4 py-2 pr-8 text-sm font-semibold shadow-sm ${PRIORITY_COLORS[item.priority]} ${canEdit ? "cursor-pointer" : ""}`}
                    >
                      {(Object.keys(PRIORITY_LABELS) as KpiActionPriority[]).map((p) => (
                        <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>
                      ))}
                    </select>
                    <ChevronDown size={13} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2" />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label>Tydzień realizacji</Label>
                  <div className="pt-1.5">
                    <WeekSelect
                      value={item.week_start}
                      onChange={(v) => canEdit && patch({ due_date: v })}
                      placeholder="— do następnego KPI —"
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label>Odpowiedzialny</Label>
                <div className="max-w-xs">
                  <SearchableSelect
                    options={userOptions}
                    value={item.assignee ?? ""}
                    placeholder="— bez odpowiedzialnego —"
                    onChange={(e) => canEdit && patch({ assignee: e.target.value || null })}
                  />
                </div>
              </div>

              {children.length > 0 && (
                <div className="flex flex-col gap-1.5">
                  <Label>Podzadania ({children.length})</Label>
                  <ul className="flex flex-col gap-1">
                    {children.map((c) => (
                      <li key={c.id}>
                        <button
                          type="button"
                          onClick={() => onOpenOther(c.id)}
                          className="flex w-full items-center gap-2 rounded px-2 py-1 text-left text-sm hover:bg-[var(--color-primary-dim)]/50"
                        >
                          <span className={`h-2 w-2 flex-shrink-0 rounded-full ${isKpiUnfinished(c.status) ? "bg-blue-400" : "bg-emerald-500"}`} />
                          <span className={isKpiUnfinished(c.status) ? "" : "text-gray-400 line-through"}>
                            {c.description}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <PingSection item={item} connectionId={connectionId} />

              <div className="flex flex-col gap-1 border-t border-gray-100 pt-4 font-mono text-[11px] text-gray-400 dark:border-gray-700 dark:text-gray-500">
                <span>Zaplanowane na KPI #{item.review_sequence_no}</span>
                <span>
                  Utworzone {fmtDateTime(item.created_at)}
                  {item.created_by_name ? ` · ${item.created_by_name}` : ""}
                </span>
                {item.done_at && (
                  <span>
                    Zrobione {fmtDate(item.done_at)}
                    {item.done_by_name ? ` · ${item.done_by_name}` : ""}
                    {item.done_review_sequence_no != null ? ` · po KPI #${item.done_review_sequence_no}` : ""}
                  </span>
                )}
              </div>

              {canEdit && (
                <div className="border-t border-gray-100 pt-4 dark:border-gray-700">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      if (!confirm("Usunąć zadanie razem z podzadaniami?")) return;
                      m.remove.mutate(item.id, { onSuccess: onClose });
                    }}
                  >
                    <Trash2 size={13} />
                    Usuń zadanie
                  </Button>
                </div>
              )}
            </div>

            {/* Rozmowa */}
            <div className="flex w-full flex-col md:w-[22rem]">
              <div className="border-b border-gray-100 px-4 py-3 dark:border-gray-700">
                <Label>Rozmowa ({comments.length})</Label>
              </div>
              <div className="flex-1 overflow-y-auto px-4 py-3">
                {comments.length === 0 ? (
                  <p className="text-sm text-gray-400 dark:text-gray-500">Brak komentarzy. Napisz pierwszy.</p>
                ) : (
                  comments.map((c) => {
                    const author = users.find((u) => u.id === c.author_id);
                    const mine = !!me && c.author_id === me.id;
                    return (
                      <div key={c.id} className="mb-3 flex gap-2">
                        <UserAvatar
                          userId={c.author_id ?? undefined}
                          fullName={c.author_name}
                          profilePictureUrl={author?.profile_picture_url}
                          size="xs"
                        />
                        <div className="min-w-0 flex-1">
                          <p className="font-mono text-[11px] text-gray-400 dark:text-gray-500">
                            {c.author_name} · {fmtDateTime(c.created_at)}{c.is_edited ? " · edytowany" : ""}
                          </p>
                          <CommentBody content={c.content} mentions={c.mentions ?? []} />
                          {mine && (
                            <button
                              type="button"
                              onClick={() => { if (confirm("Usunąć komentarz?")) cm.remove.mutate(c.id); }}
                              className="mt-0.5 text-[11px] text-gray-300 hover:text-red-500 dark:text-gray-600"
                            >
                              usuń
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              <div className="border-t border-gray-100 p-3 dark:border-gray-700">
                <RichTextEditor
                  value={draft}
                  onChange={setDraft}
                  onMentionIdsChange={setDraftMentions}
                  mentionSuggestions={mentionSuggestions}
                  placeholder="Napisz komentarz… (wpisz @ aby oznaczyć osobę)"
                />
                <Button size="sm" className="mt-2" disabled={!draft.trim() || cm.create.isPending} onClick={sendComment}>
                  Wyślij
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
